/**
 * Android AR handoff — build a Google Scene Viewer intent for a hosted GLB.
 *
 * Why this exists: <model-viewer> gates its own AR button behind `canActivateAR`,
 * which comes back false on some Android browsers (e.g. Samsung Internet) even on
 * devices that fully support ARCore / Scene Viewer. When that happens we silently
 * disable the button and the user sees nothing — the long-standing "no AR on my
 * Android" report. Launching the Scene Viewer intent ourselves hands off
 * unconditionally: Scene Viewer renders when ARCore is present, and prompts to
 * install "Google Play Services for AR" when it isn't, instead of the entry point
 * vanishing. The GLB is already hosted at a real https URL with Content-Type
 * model/gltf-binary (awm-api wallPreview/arModel), which is all Scene Viewer needs.
 *
 * Ref: https://developers.google.com/ar/develop/scene-viewer
 */

export const isAndroid = (ua = navigator.userAgent) => /android/i.test(ua);

/**
 * Build the `intent://` URL that launches Scene Viewer for a hosted .glb.
 *
 * @param {string} glbUrl      absolute https URL of the GLB (served model/gltf-binary)
 * @param {string} fallbackUrl where Scene Viewer sends the user if it cannot launch
 * @returns {string} the intent URL, or '' when glbUrl is missing
 */
export const buildSceneViewerIntent = (glbUrl, fallbackUrl) => {
  if (!glbUrl) return '';
  const params = new URLSearchParams({
    file: glbUrl,
    mode: 'ar_only',
    resizable: 'false', // keep real-world scale — matches the viewer's ar-scale="fixed"
  });
  const fallback = encodeURIComponent(fallbackUrl || glbUrl);
  return (
    `intent://arvr.google.com/scene-viewer/1.0?${params.toString()}` +
    '#Intent;scheme=https;package=com.google.ar.core;' +
    'action=android.intent.action.VIEW;' +
    `S.browser_fallback_url=${fallback};end;`
  );
};

/**
 * Navigate the current tab to a Scene Viewer intent for the given GLB.
 * `nav` is injectable so the handoff can be unit-tested without touching location.
 *
 * @returns {string} the intent that was launched, or '' when glbUrl is missing
 */
export const launchSceneViewer = (
  glbUrl,
  fallbackUrl,
  nav = (u) => { window.location.href = u; },
) => {
  const intent = buildSceneViewerIntent(glbUrl, fallbackUrl);
  if (intent) nav(intent);
  return intent;
};
